import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';

import {ZINDEX_DND_CANVAS_DRAGGED_BLOCK} from 'styles/StyleConstants';
import {BLUE_LIGHT} from 'ui-library/Colors';
import {ElementMouseEvent} from 'util/Types';

import {DnDDragHandleProps, DRAG_HANDLE_CLASS} from './DnDDragHandle';
import {DND_BLOCK_HOVER_PREVIEW_WIDTH, DND_BLOCK_SPACING} from './DnDStyleConstants';
import {DragState} from './hooks/useDnDCanvasDragState';

export function makeMinBlockWidthStyle(minBlockWidth: number): React.CSSProperties {
  // We use flexBasis to define the minimum width of the blocks. Although flex-basis isn't strictly a minimum
  // width property, we also set the flex-shrink property of blocks to 0 and the flex-grow to 1. So this means
  // "Initially, set the size of the block to minBlockWidth. Then, if there's room, grow it along with the
  // other blocks to fit, but never shrink.". I.e. the width of the object will always be >= minBlockWidth.
  //
  // We set the flexBasis in the style prop directly instead of Aphrodite because Aphrodite dndBlockStyles don't take
  // in parameters the way we want to with props.
  return {
    flexBasis: `${minBlockWidth}px`,
  };
}

export type DnDItemRenderFunction<T> = (
  item: T,
  dragHandleProps: DnDDragHandleProps,
  index: number
) => JSX.Element;

export interface DnDBlockProps<T> {
  dragState: DragState<T>;
  blockHeight?: number;
  index: number;
  item: T;
  minBlockWidth?: number;
  onDragStart: (e: ElementMouseEvent, item: T) => void;
  onMouseEnterBlock: (hoveredItem: T) => void;
  onMouseLeaveBlock: () => void;
  renderItem: DnDItemRenderFunction<T>;
  disabled: boolean;
  disableHorizontalDrag?: boolean;
}

export interface DnDPlaceholderBlockProps {
  minBlockWidth?: number;
}

export function DnDPlaceholderBlock(props: DnDPlaceholderBlockProps): JSX.Element {
  return (
    <div
      className={css(dndBlockStyles.block)}
      style={props.minBlockWidth ? makeMinBlockWidthStyle(props.minBlockWidth) : undefined}
    />
  );
}

function chooseStyle<T>(
  dragState: DragState<T>,
  disableHorizontalDrag?: boolean
): {blockStyle: Object; hoveredPreviewStyle: Object} {
  if (!disableHorizontalDrag) {
    return {
      blockStyle: dndBlockStyles.blockHorizontalDnD,
      hoveredPreviewStyle: dndBlockStyles.hoveredPreviewBlockHorizontal,
    };
  }
  if (dragState.isDragging && dragState.dragDelta.translateY > 0) {
    return {
      blockStyle: dndBlockStyles.blockVerticalDnd,
      hoveredPreviewStyle: dndBlockStyles.hoveredPreviewBlockVerticalBottom,
    };
  }
  return {
    blockStyle: dndBlockStyles.blockVerticalDnd,
    hoveredPreviewStyle: dndBlockStyles.hoveredPreviewBlockVerticalTop,
  };
}

export default function DnDBlock<T>(props: DnDBlockProps<T>): JSX.Element {
  const {disabled, dragState, blockHeight, index, item, onDragStart, renderItem} = props;
  const handleOnDragStart = useCallback(
    (e: ElementMouseEvent) => {
      onDragStart(e, item);
    },
    [onDragStart, item]
  );

  function handleOnMouseEnter(): void {
    props.onMouseEnterBlock(item);
  }

  function handleOnMouseLeave(): void {
    props.onMouseLeaveBlock();
  }

  // The goal of the useMemo is to never re-render the renderedItem, which is why wrap it in divs bellow for
  // all DnD related interactions on blocks other than the drag handle, so when adding properties to drag handle
  // props, they should not be properties that change because of DnD interactions. Adding properties like
  // "isDragging" for example would trigger re-renders of renderedItem every time the user picks up or drops a
  // block, which is problematic for expensive-to-render layouts such as Insights (causes significant lag in
  // the Insights case).
  const renderedItem = useMemo(
    () =>
      renderItem(
        item,
        {
          className: DRAG_HANDLE_CLASS,
          onMouseDown: disabled ? noop : handleOnDragStart,
        },
        index
      ),
    [disabled, handleOnDragStart, index, item, renderItem]
  );

  const {blockStyle, hoveredPreviewStyle} = chooseStyle(dragState, props.disableHorizontalDrag);
  return (
    <div
      className={css(
        dndBlockStyles.block,
        blockStyle,
        dragState.isDragging && item === dragState.draggedItem && dndBlockStyles.draggedBlock,
        dragState.isDragging &&
          dragState.isHovering &&
          item === dragState.hoveredItem &&
          !disabled &&
          hoveredPreviewStyle
      )}
      onMouseOver={disabled ? undefined : handleOnMouseEnter}
      onMouseLeave={disabled ? undefined : handleOnMouseLeave}
      style={{
        ...(props.minBlockWidth ? makeMinBlockWidthStyle(props.minBlockWidth) : {}),
        ...(dragState.isDragging && item === dragState.draggedItem
          ? {
              transform: `translate3d(${
                props.disableHorizontalDrag ? 0 : dragState.dragDelta.translateX
              }px, ${dragState.dragDelta.translateY}px, 0)`,
            }
          : {}),
        height: blockHeight,
      }}
      {...getTestProps({component: 'DnDBlock', element: 'root'})}
    >
      <div
        className={classNames(
          {'u-no-mouse-target': dragState.isDragging},
          css(dndBlockStyles.mouseEventWrapper)
        )}
        {...getTestProps({component: 'DnDBlock', element: 'mouseEventWrapper'})}
      >
        {renderedItem}
      </div>
    </div>
  );
}

export const dndBlockStyles = StyleSheet.create({
  block: {
    flexShrink: 0,
    flexGrow: 1,
    // min-width needed to override "auto" behavior, which might make the min-width the size of the blocks'
    // content or in some browsers (Firefox) it becomes the full screen width in some cases
    minWidth: 0,
  },
  draggedBlock: {
    pointerEvents: 'none',
    opacity: 0.9,
    // We need to set the z-index to a value higher than anything that is rendered in the blocks in order
    // to ensure that the contents of other blocks do not render over the dragged item.
    zIndex: ZINDEX_DND_CANVAS_DRAGGED_BLOCK,
  },
  mouseEventWrapper: {
    height: '100%',
  },
  hoveredPreviewBlockHorizontal: {
    borderRightColor: BLUE_LIGHT,
  },
  hoveredPreviewBlockVerticalTop: {
    borderTopColor: BLUE_LIGHT,
  },
  hoveredPreviewBlockVerticalBottom: {
    borderBottomColor: BLUE_LIGHT,
  },
  blockHorizontalDnD: {
    marginBottom: DND_BLOCK_SPACING,
    // To achieve the block hover preview effect where there seems to be a line between columns we play with the
    // css spacing and borders like so like so:
    // 1. We split the column spacing in half and put half in the margin and half in the padding. So if we want
    //    16px of spacing between each column, we would put 8px of right padding and 8px of right margin
    // 2. The hovered preview is achieved with a right border which will surround the padded div and leave
    //    some margin out. So now we have a "centered" div
    // 3. The problem now is that the border is part of the spacing, so the spacing with the preview would be
    //    8px padding + 8px margin + 3px border and without preview, it would just be 16px. This makes the
    //    row "move" when rendering the preview. To fix the problem we simply draw a transparent border on the
    //    item, and re-color it when we want to show the preview. This way the items don't move.
    // 4. The last problem is that now we have 19px of spacing (8 + 8 + 3). So we take the border width, divide
    //    it by 2, and then we subtract that quantity from the padding and margin to give a centered border and
    //    16px of total spacing
    borderRight: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,
    paddingRight: DND_BLOCK_SPACING / 2 - DND_BLOCK_HOVER_PREVIEW_WIDTH / 2,
    marginRight: DND_BLOCK_SPACING / 2 - DND_BLOCK_HOVER_PREVIEW_WIDTH / 2,
  },
  blockVerticalDnd: {
    borderTop: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,
    borderBottom: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,

    // Negative margin to cancel out border
    marginTop: -1 * DND_BLOCK_HOVER_PREVIEW_WIDTH,
    marginBottom: -1 * DND_BLOCK_HOVER_PREVIEW_WIDTH,
  },
});
